import { useState, useMemo, useId, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  ShieldOff,
  ChevronDown,
  Loader2,
  Plus,
  Settings2,
  FlaskConical,
  Play,
  Pencil,
  Trash2,
  Clock,
  X,
  AlertTriangle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import {
  useChannelStatus,
  useSyncLog,
  useSyncConnection,
  usePushRate,
  usePushToChannels,
  useRetrySyncLog,
  useSyncSchedules,
  useCreateSyncSchedule,
  useUpdateSyncSchedule,
  useDeleteSyncSchedule,
  useTriggerSyncSchedule,
  useConnectionDetail,
  useUpdateConnection,
  useUnitMappings,
} from '@/hooks/api/useChannels';
import { useProperties } from '@/hooks/api/useProperties';
import { useStatusPill } from '@/hooks/api/useChannelSync';
import { AxisRoomsSyncPanel } from '@/components/channels/AxisRoomsSyncPanel';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { showToast } from '@/lib/toast';
import { extractErrorMessage } from '@/lib/error';
import type { SyncSchedule } from '@/services/api/channels.api';

// ---- Constants ----

const ALLOWED_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.REVENUE_MANAGER];

const SYNC_STATUS_STYLES: Record<string, string> = {
  success: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  partial: 'bg-yellow-100 text-yellow-700',
};
const SYNC_STATUS_LABELS: Record<string, string> = { success: 'Success', failed: 'Failed', partial: 'Partial' };
const SYNC_TYPE_LABELS: Record<string, string> = { availability: 'Availability', rate: 'Rate', booking: 'Booking' };

// ---- Helpers ----

function fmtDateTime(iso: string): string {
  try { return format(parseISO(iso), 'MMM d, yyyy HH:mm'); } catch { return iso; }
}

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ---- Access Denied ----

function AccessDeniedCard() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-white py-24 px-6 text-center gap-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50">
        <ShieldOff size={32} className="text-red-400" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-400 max-w-sm">Channel Manager is restricted to Admins and Revenue Managers.</p>
      </div>
    </div>
  );
}

// ---- OTA Connection Card ----

function OtaCard({ connection, onSync, isSyncing, onManageMappings, onSetupMappings, onEdit }: { connection: Record<string, unknown>; onSync: () => void; isSyncing: boolean; onManageMappings?: () => void; onSetupMappings?: () => void; onEdit?: (() => void) | undefined }) {
  const toggleId = useId();
  const isConnected = (connection.isConnected as boolean) ?? false;
  const isActive = (connection.isActive as boolean) ?? false;
  const isSandbox = (connection.sandboxMode as boolean) ?? false;
  const label = (connection.label as string) ?? (connection.channel as string) ?? '';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col gap-4" aria-label={`${label} channel connection`}>
      <div className="flex items-start justify-between">
        <div className={cn('flex items-center justify-center w-14 h-14 rounded-xl font-bold text-sm', (connection.bgColor as string) ?? 'bg-gray-500', (connection.textColor as string) ?? 'text-white')} aria-hidden="true">
          {label}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', isConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
            {isConnected ? <CheckCircle2 size={12} aria-hidden="true" /> : <XCircle size={12} aria-hidden="true" />}
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {isSandbox && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              <FlaskConical size={10} aria-hidden="true" />
              Sandbox
            </span>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-sm text-gray-500">
          <span className="font-medium text-gray-700">{(connection.totalListings as number) ?? 0}</span> rooms mapped
          {(connection.poolSize as number | null | undefined) != null && (
            <> · shared pool of <span className="font-medium text-gray-700">{connection.poolSize as number}</span></>
          )}
        </p>
        <p className="text-xs text-gray-400">Last sync: {connection.lastSyncAt ? fmtRelative(connection.lastSyncAt as string) : 'Never'}</p>
      </div>

      {/* Unmapped-rooms guidance — availability sync fails with "No room ID mapped"
          until each room is linked to a channel room ID. Make that plain. */}
      {((connection.totalListings as number) ?? 0) === 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 flex items-start gap-2" role="status">
          <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
          <p className="text-xs text-amber-800">
            No rooms mapped yet — availability can&apos;t sync until you map your rooms to {label}.
            {onSetupMappings && (
              <button type="button" onClick={onSetupMappings} className="ml-1 font-semibold underline hover:text-amber-900">
                Set up room mapping
              </button>
            )}
          </p>
        </div>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onSync} disabled={!isConnected || isSyncing} className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500', !isConnected || isSyncing ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-primary-300 text-primary-600 hover:bg-primary-50')} aria-label={`Sync ${label} now`}>
            <RefreshCw size={12} className={isSyncing ? 'animate-spin' : ''} aria-hidden="true" />
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
          {isConnected && onManageMappings && (
            <button type="button" onClick={onManageMappings} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" aria-label={`Manage ${label} mappings`}>
              <Settings2 size={12} aria-hidden="true" />
              Mappings
            </button>
          )}
          {onEdit && (
            <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" aria-label={`Edit ${label} settings`}>
              <Pencil size={12} aria-hidden="true" />
              Edit
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor={toggleId} className="text-xs text-gray-500 select-none">Active</label>
          <span className={cn('relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent cursor-default', isActive ? 'bg-primary-600' : 'bg-gray-200')}>
            <span aria-hidden="true" className={cn('pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out', isActive ? 'translate-x-4' : 'translate-x-0')} />
          </span>
        </div>
      </div>
    </div>
  );
}

// ---- Edit AxisRooms connection ----

/**
 * Edit an AxisRooms connection's Hotel ID + rate-plan IDs. Fetches the current
 * non-secret config (via the detail endpoint) to PRE-FILL the form, so changing
 * a live sync config is a deliberate edit, not a blind overwrite. The shared
 * BestAcco access key / channel aren't shown — they're applied server-side.
 */
function EditAxisRoomsModal({
  connectionId,
  onClose,
}: {
  connectionId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useConnectionDetail(connectionId);
  const update = useUpdateConnection();
  const [hotelId, setHotelId] = useState('');
  const [ratePlanIds, setRatePlanIds] = useState('');
  const hotelInputId = useId();
  const rateInputId = useId();

  // Pre-fill once the current config loads.
  useEffect(() => {
    if (data?.axisRooms) {
      setHotelId(data.axisRooms.hotelId ?? '');
      setRatePlanIds((data.axisRooms.ratePlanIds ?? []).join(', '));
    }
  }, [data]);

  const parsedRatePlans = ratePlanIds
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const valid = hotelId.trim().length > 0 && parsedRatePlans.length > 0;

  function save() {
    if (!valid) return;
    update.mutate(
      {
        id: connectionId,
        data: {
          externalPropertyId: hotelId.trim(),
          credentials: { hotelId: hotelId.trim(), ratePlanIds: parsedRatePlans },
        },
      },
      {
        onSuccess: () => {
          showToast('AxisRooms settings updated.');
          onClose();
        },
        onError: (err) => showToast(extractErrorMessage(err), 'error'),
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="edit-axis-title">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 id="edit-axis-title" className="text-base font-semibold text-gray-900">Edit AxisRooms settings</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Loading current settings…
            </div>
          ) : (
            <>
              <div>
                <label htmlFor={hotelInputId} className="block text-xs font-medium text-gray-600 mb-1">Hotel ID</label>
                <input id={hotelInputId} type="text" value={hotelId} onChange={(e) => setHotelId(e.target.value)} placeholder="e.g. 234"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label htmlFor={rateInputId} className="block text-xs font-medium text-gray-600 mb-1">Rate plan IDs</label>
                <input id={rateInputId} type="text" value={ratePlanIds} onChange={(e) => setRatePlanIds(e.target.value)} placeholder="e.g. 1, 2"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                <p className="mt-1 text-xs text-gray-400">Comma-separated, from AxisRooms.</p>
              </div>
              <p className="text-xs text-gray-400">The AxisRooms access key &amp; channel are shared BestAcco keys, applied automatically.</p>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button type="button" onClick={onClose} disabled={update.isPending} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={save} disabled={!valid || update.isPending || isLoading}
            className={cn('inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white', !valid || update.isPending || isLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700')}>
            {update.isPending && <Loader2 size={14} className="animate-spin" />}
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Room Mappings (read-only view) ----

function MappingsModal({
  connectionId,
  unitNames,
  onClose,
}: {
  connectionId: string;
  unitNames: Map<string, string>;
  onClose: () => void;
}) {
  const { data, isLoading } = useUnitMappings(connectionId);
  const mappings = (Array.isArray(data) ? data : []).filter(
    (m: Record<string, unknown>) => (m.isActive as boolean) !== false,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="mappings-title">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 id="mappings-title" className="text-base font-semibold text-gray-900">Room mappings</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-5">
          {isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Loading mappings…
            </div>
          ) : mappings.length === 0 ? (
            <p className="py-6 text-sm text-gray-500">No rooms mapped yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Room</th>
                  <th className="pb-2 font-medium">Channel room ID</th>
                  <th className="pb-2 font-medium text-right">Rooms</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m: Record<string, unknown>) => (
                  <tr key={m.id as string} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 text-gray-800">{unitNames.get(m.unitId as string) ?? (m.unitId as string)}</td>
                    <td className="py-2 font-mono text-xs text-gray-600">{m.externalListingId as string}</td>
                    <td className="py-2 text-right font-medium text-gray-800">{(m.allotment as number) ?? 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex justify-end border-t border-gray-100 px-5 py-4">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
}

// ---- Sync Activity Log ----

function SyncActivityLog({ entries }: { entries: Array<Record<string, unknown>> }) {
  const retryMutation = useRetrySyncLog();
  const [retryingId, setRetryingId] = useState<string | null>(null);

  function handleRetry(id: string) {
    setRetryingId(id);
    retryMutation.mutate(id, { onSettled: () => setRetryingId(null) });
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-800">Sync Activity Log</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100" aria-label="Sync activity log">
          <thead>
            <tr className="bg-gray-50">
              {['Timestamp', 'Channel', 'Sync Type', 'Status', 'Details', 'Action'].map((col) => (
                <th key={col} scope="col" className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.map((entry) => {
              const status = (entry.status as string) ?? '';
              const canRetry = status === 'failed' || status === 'partial';
              const id = entry.id as string;
              return (
              <tr key={id} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">{fmtDateTime((entry.timestamp as string) ?? (entry.createdAt as string) ?? '')}</td>
                <td className="px-5 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{(entry.channelLabel as string) ?? (entry.channel as string) ?? ''}</td>
                <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">{SYNC_TYPE_LABELS[(entry.syncType as string)] ?? (entry.syncType as string) ?? ''}</td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', SYNC_STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600')}>
                    {SYNC_STATUS_LABELS[status] ?? status}
                  </span>
                </td>
                <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">{(entry.details as string) ?? ''}</td>
                <td className="px-5 py-3 whitespace-nowrap">
                  {canRetry ? (
                    <button
                      type="button"
                      onClick={() => handleRetry(id)}
                      disabled={retryingId === id}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw size={12} aria-hidden="true" className={retryingId === id ? 'animate-spin' : ''} />
                      Retry
                    </button>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Rate Push Section ----

function RatePushSection({ units }: { units: Array<Record<string, unknown>> }) {
  const unitSelectId = useId();
  const fromDateId = useId();
  const toDateId = useId();
  const rateInputId = useId();

  const [unitId, setUnitId] = useState((units[0]?.id as string) ?? '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rate, setRate] = useState('');
  const [channels, setChannels] = useState<Record<string, boolean>>({ airbnb: true, booking_com: true, expedia: false });

  const pushRateMutation = usePushRate();
  const pushToChannelsMutation = usePushToChannels();

  function toggleChannel(ch: string) {
    setChannels((prev) => ({ ...prev, [ch]: !prev[ch] }));
  }

  function handlePush() {
    if (!unitId || !dateFrom || !dateTo || !rate) return;
    const selectedChannels = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    // Backend accepts one channel at a time or omit for all channels.
    // Push to each selected channel individually.
    for (const ch of selectedChannels) {
      pushRateMutation.mutate({
        unitId,
        dates: [{ from: dateFrom, to: dateTo }],
        rate: parseFloat(rate),
        channel: ch,
      });
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <h2 className="text-base font-semibold text-gray-800 mb-4">Push Rate Update</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label htmlFor={unitSelectId} className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
          <div className="relative">
            <select id={unitSelectId} value={unitId} onChange={(e) => setUnitId(e.target.value)} className={cn('w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')}>
              {units.map((u) => (<option key={u.id as string} value={u.id as string}>{(u.propertyName as string) ?? ''} - {(u.name as string) ?? ''}</option>))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
          </div>
        </div>
        <div>
          <label htmlFor={fromDateId} className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
          <input id={fromDateId} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={cn('w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')} />
        </div>
        <div>
          <label htmlFor={toDateId} className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
          <input id={toDateId} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={cn('w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')} />
        </div>
        <div>
          <label htmlFor={rateInputId} className="block text-xs font-medium text-gray-600 mb-1">New Rate (USD)</label>
          <input id={rateInputId} type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0.00" className={cn('w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800', 'placeholder:text-gray-400', 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500')} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-4">
        <fieldset>
          <legend className="sr-only">Select channels to push rate to</legend>
          <div className="flex items-center gap-4">
            {([['airbnb', 'Airbnb'], ['booking_com', 'Booking.com'], ['expedia', 'Expedia']] as [string, string][]).map(([key, label]) => (
              <label key={key} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
                <input type="checkbox" checked={channels[key] ?? false} onChange={() => toggleChannel(key)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        <button type="button" onClick={handlePush} disabled={pushRateMutation.isPending || !unitId || !dateFrom || !dateTo || !rate} className={cn('ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2', pushRateMutation.isPending || !unitId || !dateFrom || !dateTo || !rate ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700')}>
          {pushRateMutation.isPending && <RefreshCw size={14} className="animate-spin" aria-hidden="true" />}
          {pushRateMutation.isPending ? 'Pushing...' : 'Push Rate'}
        </button>
        <button
          type="button"
          onClick={() => unitId && pushToChannelsMutation.mutate({ unitId })}
          disabled={pushToChannelsMutation.isPending || !unitId}
          title="Sync this unit's availability (and any pending rate) to all channels now"
          className={cn('inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2', pushToChannelsMutation.isPending || !unitId ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-primary-600 text-primary-700 hover:bg-primary-50')}
        >
          {pushToChannelsMutation.isPending && <RefreshCw size={14} className="animate-spin" aria-hidden="true" />}
          {pushToChannelsMutation.isPending ? 'Pushing...' : 'Push to channels'}
        </button>
      </div>
      {pushRateMutation.isSuccess && (
        <p className="mt-3 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" role="status" aria-live="polite">Rate pushed successfully.</p>
      )}
    </div>
  );
}

// ---- Sync Schedules constants ----

const CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking_com', label: 'Booking.com' },
  { value: 'expedia', label: 'Expedia' },
];

const SYNC_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'availability', label: 'Availability' },
  { value: 'rate', label: 'Rate' },
  { value: 'booking', label: 'Booking' },
];

const INTERVAL_OPTIONS: { value: number; label: string }[] = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hr' },
  { value: 120, label: '2 hr' },
  { value: 240, label: '4 hr' },
  { value: 360, label: '6 hr' },
  { value: 720, label: '12 hr' },
  { value: 1440, label: '24 hr' },
];

const CHANNEL_LABELS: Record<string, string> = {
  airbnb: 'Airbnb',
  booking_com: 'Booking.com',
  expedia: 'Expedia',
};

const SCHEDULE_STATUS_STYLES: Record<string, string> = {
  success: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

function formatInterval(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hrs = minutes / 60;
  return hrs === 1 ? '1 hr' : `${hrs} hr`;
}

// ---- Add Schedule Form (SlideOver) ----

function AddScheduleSlideOver({
  isOpen,
  onClose,
  propertyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
}) {
  const headingId = useId();
  const channelId = useId();
  const syncTypeId = useId();
  const intervalId = useId();

  const [channel, setChannel] = useState(CHANNEL_OPTIONS[0].value);
  const [syncType, setSyncType] = useState(SYNC_TYPE_OPTIONS[0].value);
  const [intervalMinutes, setIntervalMinutes] = useState(INTERVAL_OPTIONS[2].value);

  const createMutation = useCreateSyncSchedule();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate(
      { propertyId, channel, syncType, intervalMinutes },
      {
        onSuccess: () => {
          setChannel(CHANNEL_OPTIONS[0].value);
          setSyncType(SYNC_TYPE_OPTIONS[0].value);
          setIntervalMinutes(INTERVAL_OPTIONS[2].value);
          onClose();
        },
      },
    );
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden="true" onClick={onClose} />
      <aside
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-xl border-l border-gray-200 flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <h2 id={headingId} className="text-lg font-semibold text-gray-900">Add Sync Schedule</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="px-5 py-4 space-y-4 flex-1 overflow-y-auto">
            <div>
              <label htmlFor={channelId} className="block text-xs font-medium text-gray-600 mb-1">
                Channel <span aria-label="required">*</span>
              </label>
              <div className="relative">
                <select
                  id={channelId}
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  className={cn(
                    'w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  )}
                >
                  {CHANNEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
              </div>
            </div>

            <div>
              <label htmlFor={syncTypeId} className="block text-xs font-medium text-gray-600 mb-1">
                Sync Type <span aria-label="required">*</span>
              </label>
              <div className="relative">
                <select
                  id={syncTypeId}
                  value={syncType}
                  onChange={(e) => setSyncType(e.target.value)}
                  className={cn(
                    'w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  )}
                >
                  {SYNC_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
              </div>
            </div>

            <div>
              <label htmlFor={intervalId} className="block text-xs font-medium text-gray-600 mb-1">
                Interval <span aria-label="required">*</span>
              </label>
              <div className="relative">
                <select
                  id={intervalId}
                  value={intervalMinutes}
                  onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                  className={cn(
                    'w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                  )}
                >
                  {INTERVAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                createMutation.isPending ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700',
              )}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Schedule'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}

// ---- Edit Schedule Inline ----

function EditScheduleRow({
  schedule,
  onCancel,
}: {
  schedule: SyncSchedule;
  onCancel: () => void;
}) {
  const intervalId = useId();
  const [intervalMinutes, setIntervalMinutes] = useState(schedule.intervalMinutes);
  const updateMutation = useUpdateSyncSchedule();

  function handleSave() {
    updateMutation.mutate(
      { id: schedule.id, payload: { intervalMinutes } },
      { onSuccess: onCancel },
    );
  }

  return (
    <tr className="bg-primary-50">
      <td className="px-5 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
        {CHANNEL_LABELS[schedule.channel] ?? schedule.channel}
      </td>
      <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">
        {SYNC_TYPE_LABELS[schedule.syncType] ?? schedule.syncType}
      </td>
      <td className="px-5 py-3 whitespace-nowrap">
        <label htmlFor={intervalId} className="sr-only">Interval</label>
        <select
          id={intervalId}
          value={intervalMinutes}
          onChange={(e) => setIntervalMinutes(Number(e.target.value))}
          className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {INTERVAL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </td>
      <td className="px-5 py-3 whitespace-nowrap" />
      <td className="px-5 py-3 whitespace-nowrap" />
      <td className="px-5 py-3 whitespace-nowrap" />
      <td className="px-5 py-3 whitespace-nowrap" />
      <td className="px-5 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="px-3 py-1 rounded-lg text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 rounded-lg text-xs font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---- Sync Schedules Section ----

function SyncSchedulesSection({ propertyId }: { propertyId: string }) {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: schedulesData, isLoading } = useSyncSchedules();
  const updateMutation = useUpdateSyncSchedule();
  const deleteMutation = useDeleteSyncSchedule();
  const triggerMutation = useTriggerSyncSchedule();

  const schedules: SyncSchedule[] = useMemo(() => {
    if (!schedulesData) return [];
    return Array.isArray(schedulesData) ? schedulesData : [];
  }, [schedulesData]);

  function handleToggleActive(schedule: SyncSchedule) {
    updateMutation.mutate({
      id: schedule.id,
      payload: { isActive: !schedule.isActive },
    });
  }

  function handleDelete(id: string) {
    deleteMutation.mutate(id);
  }

  function handleTrigger(id: string) {
    triggerMutation.mutate(id);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="animate-spin text-primary-600" aria-hidden="true" />
        <span className="ml-2 text-sm text-gray-500">Loading schedules...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">Sync Schedules</h2>
        <button
          type="button"
          onClick={() => setIsAddOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <Plus size={16} aria-hidden="true" />
          Add Schedule
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100" aria-label="Sync schedules">
            <thead>
              <tr className="bg-gray-50">
                {['Channel', 'Sync Type', 'Interval', 'Active', 'Last Run', 'Status', 'Next Run', 'Actions'].map(
                  (col) => (
                    <th
                      key={col}
                      scope="col"
                      className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schedules.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                        <Clock size={24} className="text-gray-400" aria-hidden="true" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">No schedules configured</h3>
                      <p className="text-sm text-gray-500 max-w-xs">
                        Create a sync schedule to automatically synchronize data with your OTA channels.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                schedules.map((schedule) =>
                  editingId === schedule.id ? (
                    <EditScheduleRow
                      key={schedule.id}
                      schedule={schedule}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <tr key={schedule.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {CHANNEL_LABELS[schedule.channel] ?? schedule.channel}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {SYNC_TYPE_LABELS[schedule.syncType] ?? schedule.syncType}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatInterval(schedule.intervalMinutes)}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(schedule)}
                          role="switch"
                          aria-checked={schedule.isActive}
                          aria-label={`${schedule.isActive ? 'Deactivate' : 'Activate'} ${CHANNEL_LABELS[schedule.channel] ?? schedule.channel} ${SYNC_TYPE_LABELS[schedule.syncType] ?? schedule.syncType} schedule`}
                          className={cn(
                            'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent cursor-pointer transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                            schedule.isActive ? 'bg-primary-600' : 'bg-gray-200',
                          )}
                        >
                          <span
                            aria-hidden="true"
                            className={cn(
                              'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out',
                              schedule.isActive ? 'translate-x-4' : 'translate-x-0',
                            )}
                          />
                        </button>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {schedule.lastRunAt ? fmtDateTime(schedule.lastRunAt) : '--'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {schedule.lastSyncStatus ? (
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                              SCHEDULE_STATUS_STYLES[schedule.lastSyncStatus] ?? 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {schedule.lastSyncStatus.charAt(0).toUpperCase() + schedule.lastSyncStatus.slice(1)}
                            {schedule.consecutiveFailures > 0 && (
                              <span className="ml-1">({schedule.consecutiveFailures})</span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            Never run
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {schedule.nextRunAt ? fmtDateTime(schedule.nextRunAt) : '--'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleTrigger(schedule.id)}
                            disabled={triggerMutation.isPending}
                            className={cn(
                              'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                              triggerMutation.isPending
                                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                                : 'border-green-300 text-green-700 hover:bg-green-50',
                            )}
                            aria-label={`Run ${CHANNEL_LABELS[schedule.channel] ?? schedule.channel} ${SYNC_TYPE_LABELS[schedule.syncType] ?? schedule.syncType} now`}
                          >
                            <Play size={12} aria-hidden="true" />
                            Run Now
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(schedule.id)}
                            className="inline-flex items-center p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                            aria-label={`Edit ${CHANNEL_LABELS[schedule.channel] ?? schedule.channel} ${SYNC_TYPE_LABELS[schedule.syncType] ?? schedule.syncType} schedule`}
                          >
                            <Pencil size={14} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(schedule.id)}
                            disabled={deleteMutation.isPending}
                            className="inline-flex items-center p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed"
                            aria-label={`Delete ${CHANNEL_LABELS[schedule.channel] ?? schedule.channel} ${SYNC_TYPE_LABELS[schedule.syncType] ?? schedule.syncType} schedule`}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ),
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddScheduleSlideOver
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        propertyId={propertyId}
      />
    </div>
  );
}

// ---- Tab types ----

type ChannelTab = 'overview' | 'schedules' | 'sync';

const TAB_ITEMS: { key: ChannelTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'schedules', label: 'Schedules' },
  { key: 'sync', label: 'Sync' },
];

// ---- Page ----

export function ChannelManagerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ChannelTab>(
    searchParams.get('tab') === 'sync' ? 'sync' : searchParams.get('tab') === 'schedules' ? 'schedules' : 'overview',
  );
  const [editConnId, setEditConnId] = useState<string | null>(null);
  const [mappingsConnId, setMappingsConnId] = useState<string | null>(null);

  const { user } = useAuth();
  const hasAccess = user?.role && ALLOWED_ROLES.includes(user.role as UserRole);
  const propertyId = useSelectedPropertyId();

  const { data: statusPillData } = useStatusPill(propertyId, !!(hasAccess && propertyId));
  const syncAlerts = (statusPillData?.externalChange ?? 0) + (statusPillData?.pushUnconfirmed ?? 0);

  const { data: channelsData, isLoading: channelsLoading } = useChannelStatus(propertyId ? { propertyId } : undefined);
  const { data: syncLogData, isLoading: syncLogLoading } = useSyncLog(propertyId ? { propertyId } : undefined);
  const { data: propertiesData } = useProperties();
  const syncMutation = useSyncConnection();

  const connections: Array<Record<string, unknown>> = useMemo(() => {
    if (!channelsData) return [];
    return Array.isArray(channelsData) ? channelsData : (channelsData.data ?? []);
  }, [channelsData]);

  const syncEntries: Array<Record<string, unknown>> = useMemo(() => {
    if (!syncLogData) return [];
    return Array.isArray(syncLogData) ? syncLogData : (syncLogData.data ?? []);
  }, [syncLogData]);

  const units: Array<Record<string, unknown>> = useMemo(() => {
    if (!propertiesData) return [];
    const props = Array.isArray(propertiesData) ? propertiesData : (propertiesData.data ?? []);
    return props.flatMap((p: Record<string, unknown>) => {
      const propUnits = Array.isArray(p.units) ? p.units : [];
      return propUnits.map((u: Record<string, unknown>) => ({ ...u, propertyName: p.name, propertyId: p.id }));
    });
  }, [propertiesData]);

  const allConnected = connections.every((c) => c.isConnected);
  const anyDisconnected = connections.some((c) => !c.isConnected);

  const axisConn = connections.find((c) => (c.channel as string) === 'axis_rooms');
  const hasAxisRooms = !!axisConn;
  const mappedRooms = (axisConn?.totalListings as number) ?? 0;
  const unitNameMap = useMemo(
    () => new Map(units.map((u) => [u.id as string, u.name as string])),
    [units],
  );

  // Use the first available property ID for creating schedules
  const defaultPropertyId = useMemo(() => {
    const props = propertiesData
      ? Array.isArray(propertiesData)
        ? propertiesData
        : (propertiesData.data ?? [])
      : [];
    return (props[0] as Record<string, unknown>)?.id as string ?? '';
  }, [propertiesData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Channel Manager</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage OTA connections and distribution channels</p>
        </div>
        <div className="flex items-center gap-3">
          {hasAccess && connections.length > 0 && (
            <div className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold', allConnected ? 'bg-green-100 text-green-700' : anyDisconnected ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500')} role="status" aria-live="polite">
              <span className={cn('w-2 h-2 rounded-full', allConnected ? 'bg-green-500' : 'bg-yellow-500')} aria-hidden="true" />
              {allConnected ? 'All Channels Synced' : 'Some Channels Need Attention'}
            </div>
          )}
          {hasAccess && syncAlerts > 0 && (
            <button
              type="button"
              onClick={() => setActiveTab('sync')}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors',
                (statusPillData?.pushUnconfirmed ?? 0) > 0 ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200',
              )}
              aria-label="View AxisRooms sync"
            >
              <RefreshCw size={12} aria-hidden="true" />
              {(statusPillData?.pushUnconfirmed ?? 0) > 0
                ? `${statusPillData!.pushUnconfirmed} push${statusPillData!.pushUnconfirmed === 1 ? '' : 'es'} unconfirmed`
                : `${statusPillData?.externalChange ?? 0} changed on AxisRooms`}
            </button>
          )}
          {hasAccess && (
            <button
              type="button"
              onClick={() => navigate('/channels/connect')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <Plus size={16} aria-hidden="true" />
              Connect Channel
            </button>
          )}
        </div>
      </div>

      {!hasAccess ? (
        <AccessDeniedCard />
      ) : channelsLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading channels...</span>
        </div>
      ) : (
        <>
          {/* Tab bar */}
          <nav aria-label="Channel Manager tabs">
            <div className="flex border-b border-gray-200" role="tablist">
              {TAB_ITEMS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  aria-controls={`tabpanel-${tab.key}`}
                  id={`tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  aria-label={tab.key === 'sync' && syncAlerts > 0 ? `Sync, ${syncAlerts} need attention` : undefined}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
                    activeTab === tab.key
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  )}
                >
                  {tab.label}
                  {tab.key === 'sync' && syncAlerts > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold" aria-hidden="true">
                      {syncAlerts}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </nav>

          {/* Tab panels */}
          <div
            id="tabpanel-overview"
            role="tabpanel"
            aria-labelledby="tab-overview"
            hidden={activeTab !== 'overview'}
          >
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <section aria-label="OTA channel connections">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {connections.map((conn) => {
                      return (
                        <OtaCard
                          key={conn.id as string}
                          connection={conn}
                          onSync={() => {
                            syncMutation.mutate(conn.id as string);
                          }}
                          isSyncing={syncMutation.isPending}
                          onManageMappings={() => setMappingsConnId(conn.id as string)}
                          onSetupMappings={() => navigate('/channels/connect')}
                          onEdit={
                            (conn.channel as string) === 'axis_rooms'
                              ? () => setEditConnId(conn.id as string)
                              : undefined
                          }
                        />
                      );
                    })}
                  </div>
                </section>

                <section aria-label="Sync activity log">
                  {syncLogLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={20} className="animate-spin text-primary-600" />
                      <span className="ml-2 text-sm text-gray-500">Loading sync log...</span>
                    </div>
                  ) : (
                    <SyncActivityLog entries={syncEntries} />
                  )}
                </section>

                <section aria-label="Push rate update">
                  <RatePushSection units={units} />
                </section>
              </div>
            )}
          </div>

          <div
            id="tabpanel-schedules"
            role="tabpanel"
            aria-labelledby="tab-schedules"
            hidden={activeTab !== 'schedules'}
          >
            {activeTab === 'schedules' && (
              <SyncSchedulesSection propertyId={defaultPropertyId} />
            )}
          </div>

          <div
            id="tabpanel-sync"
            role="tabpanel"
            aria-labelledby="tab-sync"
            hidden={activeTab !== 'sync'}
          >
            {activeTab === 'sync' && (
              <AxisRoomsSyncPanel
                propertyId={propertyId}
                unitNames={unitNameMap}
                hasAxisRooms={hasAxisRooms}
                mappedRooms={mappedRooms}
              />
            )}
          </div>
        </>
      )}

      {editConnId && (
        <EditAxisRoomsModal connectionId={editConnId} onClose={() => setEditConnId(null)} />
      )}
      {mappingsConnId && (
        <MappingsModal
          connectionId={mappingsConnId}
          unitNames={new Map(units.map((u) => [u.id as string, u.name as string]))}
          onClose={() => setMappingsConnId(null)}
        />
      )}
    </div>
  );
}
